import {
  IS_BROWSER,
  getSessionStorage,
  setSessionStorage,
  deleteFromSessionStorage,
} from 'config/appConfig'
import { pageNames } from 'src/routes/pathParams'
import { getLocaleCodeFromUrl } from 'src/utils/localeUtils'

/**
 * PDP "Buy Now" → sign-in / sign-up resume (IT-43426).
 *
 * An anonymous shopper who hits Buy Now on a PDP that requires an account
 * (mysite with member pricing) is parked here before being handed over to the
 * auth flow, then brought back to that PDP with the express checkout drawer
 * reopened on the product they originally clicked.
 *
 * One sessionStorage key, one owner: the auth flow has many exits (JITSU MFE,
 * Okta /login/callback, inline sign-in reload, native /register) and the
 * resume must not depend on which one ran.
 */
const RESUME_KEY = 'buyNowSignupPdpUrl'

/** A sign-up round trip can take minutes; beyond this it is abandoned. */
const RESUME_TTL_MS = 30 * 60 * 1000

/**
 * Redirecting while the user is still inside the auth flow would abort it.
 * `/login/callback` is excluded: it drives the resume itself.
 */
const AUTH_FLOW_PATHS = [
  pageNames.signUp,
  pageNames.logoutCallback,
  pageNames.createAccount,
  pageNames.signUpModalAssociate,
]

/** Set once a resume navigation is in flight, so it cannot be raced or replayed. */
let isResumeNavigationStarted = false

/**
 * Resolves a stored url against our own origin and rejects anything that
 * escapes it (absolute, protocol-relative, backslash, encoded traversal).
 *
 * @param {string} url
 * @returns {string} same-origin path with search/hash, or '' when unsafe
 */
function toSameOriginPath(url) {
  if (!IS_BROWSER || typeof url !== 'string') {
    return ''
  }
  if (!url.startsWith('/') || url.startsWith('//') || url.includes('\\')) {
    return ''
  }
  try {
    const parsed = new URL(url, window.location.origin)
    if (parsed.origin !== window.location.origin) {
      return ''
    }
    if (decodeURIComponent(parsed.pathname).includes('..')) {
      return ''
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return ''
  }
}

/**
 * `curPageUrl` is stored without the locale segment because the router
 * basename owns it, so it has to be put back for a full page navigation.
 *
 * @param {string} pdpUrl - e.g. '/mysite/abc/product/123'
 * @returns {string} full same-origin path, '' when unsafe
 */
function toAbsolutePdpPath(pdpUrl) {
  const safePath = toSameOriginPath(pdpUrl)
  if (!safePath) {
    return ''
  }
  const [language = '', country = ''] = (getLocaleCodeFromUrl() || '').split(
    '_'
  )
  const localePrefix =
    language && country
      ? `/${country.toLowerCase()}/${language.toLowerCase()}`
      : ''
  return localePrefix && !safePath.startsWith(`${localePrefix}/`)
    ? `${localePrefix}${safePath}`
    : safePath
}

/**
 * Parks the PDP the Buy Now sign-in modal was opened from.
 *
 * @param {string} pdpUrl - value of the `curPageUrl` local storage entry
 */
export function storeBuyNowResume(pdpUrl = '') {
  if (!IS_BROWSER || !pdpUrl.includes(`${pageNames.product}/`)) {
    return
  }
  isResumeNavigationStarted = false
  setSessionStorage(RESUME_KEY, { url: pdpUrl, ts: Date.now() })
}

export function clearBuyNowResume() {
  deleteFromSessionStorage(RESUME_KEY)
}

/**
 * @returns {string} the parked PDP url, or '' when absent/expired/malformed
 */
export function getBuyNowResumeUrl() {
  if (!IS_BROWSER) {
    return ''
  }
  const entry = getSessionStorage(RESUME_KEY)
  const url = entry?.url || ''
  if (!url) {
    return ''
  }
  if (Date.now() - (entry?.ts || 0) > RESUME_TTL_MS) {
    clearBuyNowResume()
    return ''
  }
  return url
}

/**
 * Consumes the parked url when it points at the page we are already on, so the
 * PDP reopens the drawer exactly once.
 *
 * @returns {boolean} true when this page owns the pending resume
 */
export function consumeBuyNowResumeForCurrentPage() {
  const pdpUrl = getBuyNowResumeUrl()
  if (!pdpUrl) {
    return false
  }
  const absolutePath = toAbsolutePdpPath(pdpUrl)
  if (!absolutePath) {
    clearBuyNowResume()
    return false
  }
  if (absolutePath.split('?')[0] !== window.location.pathname) {
    return false
  }
  clearBuyNowResume()
  return true
}

function isAuthFlowPath(pathname = '') {
  return AUTH_FLOW_PATHS.some(path => !!path && pathname.includes(path))
}

/**
 * Sends the shopper back to the parked PDP. The entry is left in place for the
 * PDP to consume on arrival; the pathname guard makes a redirect loop
 * impossible. Must only be called once the user is authenticated.
 *
 * @returns {boolean} true when this has taken over navigation
 */
export function redirectToBuyNowResumePdp() {
  if (!IS_BROWSER) {
    return false
  }
  if (isResumeNavigationStarted) {
    return true
  }
  const pdpUrl = getBuyNowResumeUrl()
  if (!pdpUrl) {
    return false
  }
  const absolutePath = toAbsolutePdpPath(pdpUrl)
  if (!absolutePath) {
    clearBuyNowResume()
    return false
  }
  if (absolutePath.split('?')[0] === window.location.pathname) {
    return false
  }
  isResumeNavigationStarted = true
  window.location.replace(`${window.location.origin}${absolutePath}`)
  return true
}

/**
 * Safety net for auth exits that never reach LoginCallback (JITSU MFE,
 * native /register): whichever page the shopper lands on after sign-up,
 * pull them back to the parked PDP once the session is established.
 *
 * @returns {boolean} true when this has taken over navigation
 */
export function resumeBuyNowOnPageLanding() {
  if (!IS_BROWSER || isAuthFlowPath(window.location.pathname)) {
    return false
  }
  return redirectToBuyNowResumePdp()
}
